import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { I18nSearchRequest, I18nService } from "../services/i18nService";

const LOADED_FILTERS = new Set<string>();
/**
 * Custom hook for loading remote i18n translations.
 *
 * @param {I18nSearchRequest} filters - Filters for fetching remote translations
 * @param {boolean} loadOnMount - Whether to load translations immediately on mount. Defaults to true.
 * @param {boolean} override - Whether to override existing translations. Defaults to false.
 * @returns {Object} Hook state and methods
 */
export const useRemoteI18n = (
  filters: I18nSearchRequest,
  loadOnMount: boolean = true,
  override: boolean = false,
) => {
  const { t, i18n } = useTranslation();
  // const queryClient = useQueryClient();

  // Query key includes filters and language for cache separation
  const queryKey = [
    "remoteI18n",
    JSON.stringify(Object.values(filters ?? {}).sort()),
    i18n.language,
    override,
  ];

  // Fetch function for translations

  // Query for loading translations
  const {
    isLoading: loading,
    isError,
    error,
    refetch: loadTranslations,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (LOADED_FILTERS.has(JSON.stringify(queryKey))) {
        return;
      }
      await I18nService.loadRemoteTranslations(
        {
          scope: filters.entityType ? "LOCAL" : (filters.scope ?? "GLOBAL"),
          language: i18n.language,
          ...filters,
        },
        override,
      );
      LOADED_FILTERS.add(JSON.stringify(queryKey));
      return;
    },
    enabled: !!filters, //&& loadOnMount
  });

  // Mutation for refreshing translations
  // const refreshMutation = useMutation({
  //   mutationFn: async () => {
  //     if (!filters) return;
  //     await I18nService.refreshTranslations({
  //       ...filters,
  //       language: i18n.language,
  //     });
  //     // Invalidate cache to force refetch
  //     await queryClient.invalidateQueries({ queryKey });
  //     return true;
  //   },
  // });

  // // Reload when language changes
  // useEffect(() => {
  //   if (filters && loaded && filters.language !== i18n.language) {
  //     queryClient.invalidateQueries({ queryKey });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [i18n.language]);

  return {
    loading,
    error: isError ? error : null,
    loadTranslations,
    // refreshTranslations: refreshMutation.mutateAsync,
    t, // Re-export for convenience
  };
};

/**
 * Custom hook for loading entity-specific translations.
 *
 * @param {string} entityType - The type of entity (e.g., "Product", "Category")
 * @param {string} entityId - The ID of the entity
 * @param {string} language - The language code (optional, uses current i18n language if not provided)
 * @returns {Object} Hook state and translations
 */
export const useEntityTranslations = (
  entityType: string,
  entityId: string,
  language?: string,
) => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});

  const targetLanguage = language || i18n.language;

  /**
   * Loads entity-specific translations.
   */
  const loadEntityTranslations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const entityTranslations = await I18nService.getEntityTranslations(
        entityType,
        entityId,
        targetLanguage,
      );

      setTranslations(entityTranslations);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to load entity translations"),
      );
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, targetLanguage]);

  // Load translations when parameters change
  useEffect(() => {
    if (entityType && entityId && targetLanguage) {
      loadEntityTranslations();
    }
  }, [entityType, entityId, targetLanguage, loadEntityTranslations]);

  /**
   * Gets a translation for a specific key with fallback to regular t function.
   *
   * @param {string} key - The translation key
   * @param {string} fallback - Optional fallback value
   * @returns {string} The translated value
   */
  const getTranslation = (key: string, fallback?: string): string => {
    return translations[key] || fallback || key;
  };

  return {
    loading,
    error,
    translations,
    getTranslation,
    loadEntityTranslations,
  };
};
